// Action labels mapping
const ACTION_LABELS = {
  1: 'C', // Create
  2: 'R', // Read
  4: 'U', // Update
  8: 'D', // Delete
  16: 'E', // Export
  32: 'I', // Import
};

const ACTION_MAP = {
  C: 'create',
  R: 'read',
  U: 'update',
  D: 'delete',
  E: 'export',
  I: 'import',
};

// Helper: Convert bitmask to action labels array
const bitmaskToActions = (bitmask) => {
  if (!bitmask && bitmask !== 0) return [];
  const actions = [];
  Object.entries(ACTION_LABELS).forEach(([value, label]) => {
    if ((bitmask & parseInt(value)) !== 0) {
      actions.push(ACTION_MAP[label]);
    }
  });
  return actions;
};

// Helper: Convert action labels string to array
const _actionStringToActions = (actionStr) => {
  if (!actionStr) return [];
  return actionStr
    .split(',')
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean)
    .map((a) => {
      // Map abbreviated action to full name
      const map = { c: 'create', r: 'read', u: 'update', d: 'delete', e: 'export', i: 'import' };
      return map[a] || a;
    });
};

// Helper function to get permissions array from user object
const getUserPermissionsArray = (user) => {
  if (!user) return [];

  // Try different possible structures
  const perms =
    user.permissions || user.Permissions || user.user_permissions || user.UserPermissions || [];

  // Handle if permissions is wrapped in another object
  if (Array.isArray(perms)) {
    // Normalize permissions - convert bitmask to action_labels if needed
    return perms.map((p) => {
      if (p.level_action_limited && typeof p.level_action_limited === 'string') {
        // Already has string format, keep as-is
        return p;
      }
      if (p.actions && !p.level_action_limited) {
        // Has bitmask but no string format - convert it
        return {
          ...p,
          level_action_limited: bitmaskToActions(p.actions)
            .map((a) => {
              const reverseMap = {
                create: 'C',
                read: 'R',
                update: 'U',
                delete: 'D',
                export: 'E',
                import: 'I',
              };
              return reverseMap[a] || a.toUpperCase();
            })
            .join(','),
        };
      }
      return p;
    });
  }

  return [];
};

// Helper function to normalize permission path
const normalizePermissionKey = (key) => {
  if (!key) return '';
  return key.toString().trim();
};

// Helper function to build candidates from menuId and menuPath
const buildCandidates = (menuId, menuPath) => {
  const candidates = new Set();

  if (menuId) {
    candidates.add(menuId);
    candidates.add(menuId.replace(/-/g, '_'));
    candidates.add(menuId.replace(/_/g, '-'));
  }

  if (menuPath) {
    const normalizedPath = menuPath.startsWith('/') ? menuPath : `/${menuPath}`;
    const pathWithoutSlash = normalizedPath.replace(/^\/+/, '');
    candidates.add(menuPath);
    candidates.add(normalizedPath);
    candidates.add(pathWithoutSlash);
    candidates.add(pathWithoutSlash.replace(/-/g, '_'));
    candidates.add(pathWithoutSlash.replace(/_/g, '-'));
  }

  // Parent menu variants (for nested routes like dashboard-member -> dashboard)
  if (menuId) {
    const parts = menuId.replace(/^\/+/, '').split('-');
    if (parts.length > 1) {
      candidates.add(parts[0]);
      candidates.add('/' + parts[0]);
    }
  }

  return candidates;
};

// Helper function to build candidates from permission key
const buildPermissionVariants = (permKey) => {
  return [
    permKey,
    permKey.startsWith('/') ? permKey : `/${permKey}`,
    permKey.replace(/^\/+/, ''),
    permKey.replace(/-/g, '_'),
    permKey.replace(/_/g, '-'),
    permKey.replace(/^\/+/, '').replace(/-/g, '_'),
    permKey.replace(/^\/+/, '').replace(/_/g, '-'),
  ];
};

// Check if user has access to a specific permission
const hasPermissionValue = (userPermissions, candidates) => {
  if (!userPermissions || userPermissions.length === 0) return false;

  for (const p of userPermissions) {
    const permKey = normalizePermissionKey(
      p.level_menu_limited || p.level_menu || p.menu || p.path || ''
    );

    if (!permKey) continue;

    const variants = new Set(buildPermissionVariants(permKey));
    // Also include alias forms of the stored permission key
    if (expandMenuKeyAliasesFn) {
      expandMenuKeyAliasesFn(permKey).forEach((alias) => {
        buildPermissionVariants(alias).forEach((v) => variants.add(v));
      });
    }

    for (const variant of variants) {
      if (candidates.has(variant)) {
        return true;
      }
    }
  }

  return false;
};

// Dynamic import for child routes mapping (to avoid circular dependency)
let CHILD_ROUTES_TO_PARENT = {};
let getParentMenuIdFromMenuIdFn = null;
let expandMenuKeyAliasesFn = null;
try {
  const menuPermissionMap = require('helpers/menuPermissionMap');
  CHILD_ROUTES_TO_PARENT = menuPermissionMap.CHILD_ROUTES_TO_PARENT || {};
  getParentMenuIdFromMenuIdFn = menuPermissionMap.getParentMenuIdFromMenuId || null;
  expandMenuKeyAliasesFn = menuPermissionMap.expandMenuKeyAliases || null;
} catch {
  // Fallback - mapping will be empty
}

// Expand menu key with known aliases (product-vendor <-> ppob-product-vendor, etc.)
const expandWithAliases = (key, candidates) => {
  if (!key) return;
  const aliases = expandMenuKeyAliasesFn
    ? expandMenuKeyAliasesFn(key)
    : (() => {
        // Fallback alias groups if menuPermissionMap failed to load
        const groups = {
          'ppob-product-vendor': ['product-vendor'],
          'emoney-margin': ['emoney-setting-margin'],
          'ismple-area': ['isimple-area'],
          'report-group-tfsaldo': ['trx-group-tfsaldo'],
          'setting-rekomendasi-limit': ['setting-rekomenasi-limit'],
          'message-customer': ['message'],
        };
        const keys = new Set([key]);
        Object.entries(groups).forEach(([canonical, alts]) => {
          if (key === canonical || alts.includes(key)) {
            keys.add(canonical);
            alts.forEach((a) => keys.add(a));
          }
        });
        return Array.from(keys);
      })();

  aliases.forEach((alias) => {
    candidates.add(alias);
    candidates.add(alias.replace(/-/g, '_'));
    candidates.add(alias.replace(/_/g, '-'));
    candidates.add('/' + alias.replace(/^\/+/, ''));
  });
};

// Helper to get parent menu ID from path
const getParentMenuIdFromPath = (path) => {
  if (!path) return null;
  const cleanPath = path.split('?')[0].split('#')[0];

  // 1. Check exact match
  if (CHILD_ROUTES_TO_PARENT[cleanPath]) {
    return CHILD_ROUTES_TO_PARENT[cleanPath];
  }

  // 2. Check pattern match (e.g., /blog/detail/123 -> /blog/detail)
  const pathParts = cleanPath.split('/');
  for (let i = pathParts.length - 1; i >= 2; i--) {
    const tryPath = pathParts.slice(0, i).join('/');
    if (CHILD_ROUTES_TO_PARENT[tryPath]) {
      return CHILD_ROUTES_TO_PARENT[tryPath];
    }
  }

  return null;
};

// Helper to get parent menu ID from menu ID (for sub-menu items)
const getParentMenuIdFromMenuId = (menuId) => {
  if (!menuId) return null;
  if (getParentMenuIdFromMenuIdFn) {
    return getParentMenuIdFromMenuIdFn(menuId);
  }
  // Fallback to local SUB_MENU_PARENT_MAP (kept for backwards compatibility)
  const SUB_MENU_PARENT_MAP = {
    'monitoring-customer-history-pin': 'monitoring-customer',
    'monitoring-customer-wrong-pin': 'monitoring-customer',
    'message-customer': 'message',
    'message-guest': 'message',
    'ppob-inject-label': 'product-inject',
    'product-inject-whitelist-sn': 'product-inject',
    'data-package-label': 'product-paket-data',
    'ppob-voucher-label': 'product-voucher',
    'digipos-account': 'digipos',
    'digipos-area': 'digipos',
    'digipos-margin': 'digipos',
    'isimple-account': 'isimple',
    'isimple-area': 'isimple',
    'ismple-area': 'isimple',
    'isimple-margin': 'isimple',
    'cuanku-account': 'cuanku',
    'cuanku-area': 'cuanku',
    'cuanku-margin': 'cuanku',
    'setting-markup-global': 'markup',
    'setting-mark-up': 'markup',
    'content-category': 'content-blog',
    'content-slider-app': 'content-slider',
    'trx-group-tfsaldo': 'report-transaksi',
    'report-group-tfsaldo': 'report-transaksi',
    'report-idpel-tfbank': 'report-transaksi',
    'setting-bank-deposit': 'deposit',
  };
  return SUB_MENU_PARENT_MAP[menuId] || null;
};

export const hasPermission = (user, menuId, menuPath) => {
  // ✅ Owner bypass semua permission
  if (user?.user_level && String(user.user_level).toLowerCase() === 'owner') {
    return true;
  }

  // ✅ Get permissions from user object
  const userPermissions = getUserPermissionsArray(user);

  // ✅ Tidak ada permission = tidak bisa akses
  if (userPermissions.length === 0) {
    return false;
  }

  // Build candidates dari menuId dan menuPath
  // Jika menuPath tidak provided, derive dari menuId
  const derivedPath = menuPath || (menuId ? `/${menuId}` : '');
  const normalizedPath = derivedPath.startsWith('/') ? derivedPath : `/${derivedPath}`;

  // Generate all possible path variants
  let candidates = buildCandidates(menuId, menuPath);

  // Add alias variants (e.g. product-vendor <-> ppob-product-vendor)
  expandWithAliases(menuId, candidates);

  // Add parent menu variants if this is a child route
  const parentMenuId = getParentMenuIdFromPath(normalizedPath);
  if (parentMenuId) {
    expandWithAliases(parentMenuId, candidates);
  }

  // Add parent menu variants from menu ID (for sub-menu items like message-customer -> message)
  if (menuId) {
    const menuParentId = getParentMenuIdFromMenuId(menuId);
    if (menuParentId) {
      expandWithAliases(menuParentId, candidates);
    }
  }

  return hasPermissionValue(userPermissions, candidates);
};

export const getPermissionActions = (user, menuId, menuPath) => {
  // ✅ Owner punya semua action
  if (user?.user_level && ['owner', 'operator'].includes(String(user.user_level).toLowerCase())) {
    return ['create', 'read', 'update', 'delete', 'export', 'import'];
  }

  // Get permissions from user object
  const userPermissions = getUserPermissionsArray(user);

  // Tidak ada permission = tidak ada action
  if (userPermissions.length === 0) {
    return [];
  }

  // Build candidates dari menuId dan menuPath
  const derivedPath = menuPath || (menuId ? `/${menuId}` : '');
  const normalizedPath = derivedPath.startsWith('/') ? derivedPath : `/${derivedPath}`;

  let candidates = buildCandidates(menuId, menuPath);

  // Add alias variants (e.g. product-vendor <-> ppob-product-vendor)
  expandWithAliases(menuId, candidates);

  // Add parent menu variants if this is a child route
  const parentMenuId = getParentMenuIdFromPath(normalizedPath);
  if (parentMenuId) {
    expandWithAliases(parentMenuId, candidates);
  }

  // Add parent menu variants from menu ID (for sub-menu items like message-customer -> message)
  if (menuId) {
    const menuParentId = getParentMenuIdFromMenuId(menuId);
    if (menuParentId) {
      expandWithAliases(menuParentId, candidates);
    }
  }

  const actions = [];

  userPermissions.forEach((p) => {
    const permKey = normalizePermissionKey(
      p.level_menu_limited || p.level_menu || p.menu || p.path || ''
    );

    if (!permKey) return;

    const variants = new Set(buildPermissionVariants(permKey));
    if (expandMenuKeyAliasesFn) {
      expandMenuKeyAliasesFn(permKey).forEach((alias) => {
        buildPermissionVariants(alias).forEach((v) => variants.add(v));
      });
    }

    if ([...variants].some((v) => candidates.has(v))) {
      if (p.level_action_limited) {
        // Parse action string like "C,R,U" or "read,create" into array
        const actionArray = p.level_action_limited
          .split(',')
          .map((a) => a.trim().toLowerCase())
          .filter(Boolean)
          .map((a) => {
            const map = {
              c: 'create',
              r: 'read',
              u: 'update',
              d: 'delete',
              e: 'export',
              i: 'import',
            };
            return map[a] || a;
          });
        actions.push(...actionArray);
      }
    }
  });

  return [...new Set(actions)];
};

export const canPerformAction = (user, menuId, action) => {
  const actions = getPermissionActions(user, menuId);
  return actions.includes(action.toLowerCase());
};
